import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms'
import { IonicModule, IonInput } from '@ionic/angular'
import { format, formatISO } from 'date-fns'
import equal from 'fast-deep-equal/es6'
import { TimeSegment } from '../../models/DateTime/TimeSegment'

@Component({
  selector: 'app-time-input',
  templateUrl: './time-input.component.html',
  styleUrls: ['./time-input.component.scss'],
  host: {
    '[class.disabled]': 'disabled',
  },
  imports: [FormsModule, IonicModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TimeInputComponent),
      multi: true,
    },
  ],
})
export class TimeInputComponent implements OnChanges, ControlValueAccessor {
  static readonly TIME_MASK = 'HH:MM'
  static readonly TIME_FORMAT = 'HH:mm'
  static readonly TIME_PLACEHOLDER = 'hh:mm'

  @Input() value: TimeSegment | null = null
  @Input() disabled = false
  @Input() textAlign: 'left' | 'right' | 'center' | null = null
  @Input() postponeChangeUntilBlur: boolean = false

  @Output() changed = new EventEmitter<TimeSegment>()

  @ViewChild('timeInput') timeInput!: IonInput

  protected readonly self = TimeInputComponent
  protected uuid = crypto.randomUUID()

  protected timeInputValue = ''
  protected timePickerValue = ''
  protected hasUnhandledChange = false
  private _latestValue: TimeSegment | null = null

  private onChange: (_: any) => void = () => {}
  protected onTouched: (_: any) => void = () => {}

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.renderValue()
      this.handleChange(changes['value'].currentValue, false, !changes['value'].isFirstChange())
    }
  }

  writeValue(value: TimeSegment): void {
    this.value = value
    this.renderValue()
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: (_: any) => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  renderValue(): void {
    if (this.value) {
      this.timeInputValue = format(this.value.toDate(), TimeInputComponent.TIME_FORMAT)
      this.timePickerValue = formatISO(this.value.toDate())
    } else {
      this.timeInputValue = ''
      this.timePickerValue = ''
    }
  }

  private handleChange(newValue: TimeSegment | null, canEmitChange: boolean, canFireChange: boolean): void {
    if (!equal(newValue, this._latestValue)) {
      if (canEmitChange) {
        this.changed.emit(newValue)
      }
      if (canFireChange) {
        this.onChange(newValue)
      }
    }
    this._latestValue = newValue
  }

  async onTimeInputChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    this.timeInputValue = input.value
    if (this.timeInputValue) {
      const [hours, minutes] = this.timeInputValue.split(':').map(Number)
      const date = new Date()
      date.setHours(hours, minutes, 0, 0)
      this.value = new TimeSegment(date)
    } else {
      this.value = null
    }
    if (this.postponeChangeUntilBlur) {
      this.hasUnhandledChange = true
    } else {
      this.handleChange(this.value, true, true)
    }
  }

  async onTimeInputBlur($event: any): Promise<void> {
    if (this.hasUnhandledChange) {
      this.hasUnhandledChange = false
      this.handleChange(this.value, true, true)
    }
    this.onTouched($event)
  }
}
